/**
 * Quem é o visitante do ponto de vista do AMBIENTE: aparelho, navegador e sistema.
 *
 * Extraído de `user-guide/dto/User.ts`, que era o único lugar que sabia classificar isso. O
 * loader do `getdemo.js` avalia a audiência do Copilot por conta própria e montava o visitante
 * só com `customParams` e `language` — então toda condição de `DEVICE`, `BROWSER` ou `OS` caía
 * em `undefined` e reprovava, mesmo para quem o painel dizia ter acesso. Como os filtros de um
 * segmento se combinam com E, bastava UMA condição dessas para o agente nunca ficar elegível.
 *
 * Vive aqui, e não copiado no loader, porque duas implementações divergiriam com o tempo e o
 * mesmo visitante passaria a ser classificado de um jeito pelo guia e de outro pelo Copilot.
 */

export enum VisitorDeviceType {
  DESKTOP = 'DESKTOP',
  MOBILE = 'MOBILE',
  TABLET = 'TABLET'
}

export const detectDeviceType = (userAgent: string): VisitorDeviceType => {
  const ua = userAgent.toLowerCase();
  const isMobile = /mobile|android|iphone|ipad|phone/i.test(ua);
  const isTablet = /tablet|ipad/i.test(ua);

  if (isTablet) return VisitorDeviceType.TABLET;
  if (isMobile) return VisitorDeviceType.MOBILE;
  return VisitorDeviceType.DESKTOP;
};

export const detectBrowser = (userAgent: string): string => {
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';

  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browserName = 'Chrome';
    browserVersion = userAgent.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Firefox')) {
    browserName = 'Firefox';
    browserVersion = userAgent.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browserName = 'Safari';
    browserVersion = userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Edg')) {
    browserName = 'Edge';
    browserVersion = userAgent.match(/Edg\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) {
    browserName = 'Internet Explorer';
    browserVersion = userAgent.match(/(?:MSIE |rv:)(\d+)/)?.[1] || 'Unknown';
  }

  return `${browserName} ${browserVersion}`;
};

export const detectOS = (userAgent: string): string => {
  let osName = 'Unknown';
  let osVersion = 'Unknown';

  if (userAgent.includes('Windows')) {
    osName = 'Windows';
    if (userAgent.includes('Windows NT 10.0')) osVersion = '10';
    else if (userAgent.includes('Windows NT 6.3')) osVersion = '8.1';
    else if (userAgent.includes('Windows NT 6.2')) osVersion = '8';
    else if (userAgent.includes('Windows NT 6.1')) osVersion = '7';
  } else if (userAgent.includes('Mac OS X')) {
    osName = 'macOS';
    osVersion = userAgent.match(/Mac OS X (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Unknown';
  } else if (userAgent.includes('Linux')) {
    osName = 'Linux';
  } else if (userAgent.includes('Android')) {
    osName = 'Android';
    osVersion = userAgent.match(/Android (\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('iOS')) {
    osName = 'iOS';
    osVersion = userAgent.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Unknown';
  }

  return `${osName} ${osVersion}`;
};

/** O ambiente do visitante desta janela, no formato que o avaliador de segmentação espera. */
export const readVisitorEnvironment = (): {
  device: VisitorDeviceType | undefined;
  browser: string | undefined;
  os: string | undefined;
} => {
  try {
    const ua = navigator.userAgent;
    return { device: detectDeviceType(ua), browser: detectBrowser(ua), os: detectOS(ua) };
  } catch {
    // Sem `navigator` (SSR, worker) é melhor não afirmar nada do que afirmar errado.
    return { device: undefined, browser: undefined, os: undefined };
  }
};
